import { apiFetch } from '../lib/apiFetch';

// ─── Legacy types (used by WorkshopCreationForm) ───────────────────────────────
export interface Venue {
  id: string;
  name: string;
  description: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  website: string;
  capacity: number;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  photos: string[];
  notes: string;
  fareharborLocationId?: number;
  latitude?: number | null;
  longitude?: number | null;
}

export interface Subregion {
  id: string;
  name: string;
  code: string;
}

export interface Region {
  id: string;
  name: string;
  code: string;
  subregions: Subregion[];
}

/** The roster row, as much of it as the grouping shim reads. */
type RosterVenue = Venue & { region: { id: string; name: string } | null };

/** The bucket for venues with no operational region. */
const UNASSIGNED_REGION_ID = 'unassigned';

export async function fetchVenues(): Promise<{
  venues: Record<string, Venue[]>;
  regions: Region[];
}> {
  const res = await apiFetch('/api/v1/venues');
  if (!res.ok) {
    throw new Error(`Failed to fetch venues: ${res.status}`);
  }
  const data = await res.json();

  // The roster endpoint returns a FLAT `{ venues: [...] }`, adapted here to the
  // region-grouped shape the Create Workshop drawer's venue picker wants — its
  // only remaining consumer. Venues with no operational region land under a
  // single "Unassigned" pseudo-region.
  //
  // BON-538 phase 2 moved the region from a flat `regionId` to a nested
  // `region` object, which also gives the picker a real region NAME; it used to
  // group under raw uuids. Reading `regionId` here after that change put every
  // venue in the unassigned bucket.
  if (Array.isArray(data.venues)) {
    const venuesByRegion: Record<string, Venue[]> = {};
    const regionMap = new Map<string, Region>();
    for (const v of data.venues as RosterVenue[]) {
      const key = v.region?.id ?? UNASSIGNED_REGION_ID;
      if (!venuesByRegion[key]) venuesByRegion[key] = [];
      venuesByRegion[key].push(v);
      if (!regionMap.has(key)) {
        regionMap.set(key, {
          id: key,
          name: v.region?.name ?? 'Unassigned',
          code: '',
          subregions: [],
        });
      }
    }
    return { venues: venuesByRegion, regions: Array.from(regionMap.values()) };
  }
  // Pre-Phase-1.1 shape — pass through unchanged.
  return data;
}

// ─── Venue lifecycle + structured jsonb shapes (BON-157 contract) ──────────────
export type VenueStage = 'prospect' | 'active' | 'home' | 'inactive' | 'dropped';

/** One structured logistics field — a value plus whether teachers see it on
 *  their day-of card. Mirrors the backend `LogisticsEntry`. */
export interface VenueLogisticsEntry {
  value: string;
  teacherVisible: boolean;
}

/** Fixed key set for the `logistics` jsonb column; each entry optional. */
export type VenueLogisticsKey =
  | 'parking'
  | 'loadIn'
  | 'powerAv'
  | 'space'
  | 'leadTime'
  | 'weeknights'
  | 'blackout'
  | 'onDuty';

export type VenueLogistics = Partial<Record<VenueLogisticsKey, VenueLogisticsEntry>>;

export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export type PostingCadence = 'WEEKLY_PLUS' | 'MONTHLY' | 'DORMANT' | 'UNKNOWN';

/** One platform's presence. An absent `audienceSize` means unknown, never 0. */
export interface SocialPresence {
  handle: string;
  audienceSize?: number;
  cadence?: PostingCadence;
  /** Set by the server only. */
  lastVerifiedAt?: string;
}

/** The `social` jsonb column. Edited through the marketing endpoint only. */
export type VenueSocial = Partial<Record<SocialPlatform, SocialPresence>> & {
  website?: string;
};

// ─── Full venue detail (returned by POST /venues, GET /venues/:id, PUT /venues/:id) ─
export interface VenueDetail {
  id: string;
  regionId: string;
  name: string;
  description: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  maxCapacity: number;
  amenities: string[];
  parkingInfo: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  setupTimeMinutes: number;
  cleanupTimeMinutes: number;
  venueNotes: string | null;
  isActive: boolean;
  // BON-157 net-new fields.
  stage: VenueStage;
  droppedReason: string | null;
  domain: string | null;
  logistics: VenueLogistics | null;
  social: VenueSocial | null;
  /** Half-point rating from 1 to 5, or null when nobody has rated the venue. */
  comarketingRating: number | null;
  /** Null means unknown. */
  followingOnInstagram: boolean | null;
  hubspotUrl: string | null;
  /** Worked out by the server for the caller: may they edit its marketing? */
  canWriteMarketing: boolean;
  fareharborLocationId: string | null;
  /** Sales-tax rate as a human percentage (8.875 = 8.875%), or null when unset.
   *  Null raises the "No tax rate assigned" banner; 0 does not — a 0 rate is a
   *  confirmed no-tax jurisdiction, not a missing one (BON-387 / design.md D7). */
  taxPercent: number | null;
  createdAt: string;
  updatedAt: string;
  region: {
    id: string;
    name: string;
    code: string | null;
    parentId: string | null;
  } | null;
  // Phase 1.1+: backend emits drift + audit on every read.
  drift?: VenueDriftEntry[];
  audit?: VenueAuditEntry[];
  /** Most-recent FH item PK at this venue (Phase 1.5+). Null when the venue
   *  has no FH-sourced workshops in the warehouse. Used to build the
   *  post-save FareHarbor deep-link. */
  fareharborItemPk?: string | null;
  /** The photo the booking site shows, and whether staff uploaded it. */
  bookingPhoto: VenueBookingPhoto;
  /** Whether an imported FareHarbor photo would show if the uploaded one were removed. */
  importedPhotoAvailable: boolean;
  /** Whose text the booking site shows as the venue description. */
  descriptionSource: VenueDescriptionSource;
  /** False when the viewer may look at this venue but not change it. */
  canEdit: boolean;
}

export type VenueDescriptionSource = 'staff' | 'fareharbor' | 'nebari';

export interface VenueBookingPhoto {
  url: string | null;
  source: 'staff' | 'imported' | null;
}

export interface VenueDriftEntry {
  field: string;
  nebariValue: unknown;
  fhValue: unknown;
}

export interface VenueAuditEntry {
  id: string;
  actorUserId: string;
  actorEmail: string | null;
  changedAt: string;
  fieldChanges: Record<string, { before: unknown; after: unknown }> | unknown;
}

export interface UpdateVenueResult {
  venue: VenueDetail;
  /** True iff the patch changed at least one FH-mirrored field. UI uses this
   *  to gate the post-save reminder modal. */
  fhMirroredFieldsChanged: boolean;
}

export async function fetchVenueById(id: string): Promise<VenueDetail> {
  const res = await apiFetch(`/api/v1/venues/${id}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `Failed to fetch venue: ${res.status}`);
  }
  return res.json();
}

/** Thrown when the backend returns a 400 with a `fieldErrors` map (Zod
 *  validation failure). UI surfaces messages inline next to each field. */
export class VenueValidationError extends Error {
  fieldErrors: Record<string, string>;
  constructor(fieldErrors: Record<string, string>, message = 'Validation failed') {
    super(message);
    this.name = 'VenueValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/** Create payload (BON-157). Prospect venues need only name + domain; active
 *  venues require the full address. `regionId` is an optional override — the
 *  backend derives it from `state` when omitted. Capacity is NOT set here
 *  (it's edited on the venue detail page). */
export interface VenueCreateInput {
  stage?: 'prospect' | 'active';
  name: string;
  domain: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  regionId?: string | null;
}

export async function createVenue(data: VenueCreateInput): Promise<VenueDetail> {
  const res = await apiFetch('/api/v1/venues', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      fieldErrors?: Record<string, string>;
    };
    if (res.status === 400 && err.fieldErrors) {
      throw new VenueValidationError(err.fieldErrors, err.message ?? 'Validation failed');
    }
    throw new Error(err.error || err.message || `Failed to create venue: ${res.status}`);
  }
  return res.json();
}

/** Fields the domain-enrichment pass can suggest at create time (BON-119). */
export interface VenueEnrichment {
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

/**
 * Pull suggested venue fields from the venue's public site (BON-119).
 * The endpoint may not exist yet — callers should treat a thrown error as
 * "enrichment unavailable, fall back to manual entry".
 */
export async function enrichVenueFromDomain(domain: string): Promise<VenueEnrichment> {
  const res = await apiFetch('/api/v1/venues/enrich', {
    method: 'POST',
    body: JSON.stringify({ domain }),
  });
  if (!res.ok) {
    throw new Error(`Enrichment unavailable: ${res.status}`);
  }
  return res.json();
}

export type VenuePatch = Partial<{
  name: string;
  regionId: string | null;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  maxCapacity: number;
  description: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  parkingInfo: string | null;
  venueNotes: string | null;
  amenities: string[];
  setupTimeMinutes: number;
  cleanupTimeMinutes: number;
  // BON-157 net-new fields (venue detail page edits these).
  stage: VenueStage;
  droppedReason: string | null;
  domain: string | null;
  logistics: VenueLogistics | null;
  isActive: boolean;
  taxPercent: number;
}>;

export async function updateVenue(id: string, data: VenuePatch): Promise<UpdateVenueResult> {
  const res = await apiFetch(`/api/v1/venues/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      fieldErrors?: Record<string, string>;
    };
    if (res.status === 400 && err.fieldErrors) {
      throw new VenueValidationError(err.fieldErrors, err.message ?? 'Validation failed');
    }
    throw new Error(err.error || err.message || `Failed to update venue: ${res.status}`);
  }
  return res.json();
}

export async function deactivateVenue(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/venues/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      (err as { error?: string }).error || `Failed to deactivate venue: ${res.status}`,
    );
  }
}
